<script>
import RawData from "@/components/widgets/raw-data.vue";
export default {
    name: "StockLedger",
    components: {
        RawData
    },
    props: {
        stock: {
            type: Object,
            required: true,
        },
        transactions: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            currentPage: 1,
            perPage: 10,
            pageOptions: [10, 25, 50, 100],
            filter: "",
            fields: [
                {
                    key: "date",
                    label: "Date",
                    sortable: false,
                },
                {
                    key: "referenceNumber",
                    label: "Reference",
                    sortable: false,
                },
                {
                    key: "description",
                    label: "Description",
                    sortable: false,
                },
                {
                    key: "debit",
                    label: "Debit (In)",
                    sortable: false,
                    thStyle: { width: "120px" },
                    tdClass: "text-center",
                    thClass: "text-center",
                    formatter: (value) => {
                        return value && value > 0 ? this.formatNumber(value) : '';
                    }
                },
                {
                    key: "credit",
                    label: "Credit (Out)",
                    sortable: false,
                    thStyle: { width: "120px" },
                    tdClass: "text-center",
                    thClass: "text-center",
                    formatter: (value) => {
                        return value && value > 0 ? this.formatNumber(value) : '';
                    }
                },
                {
                    key: "balance",
                    label: "Balance",
                    sortable: false,
                    thStyle: { width: "120px" },
                    thClass: "text-center",
                    tdClass: "text-center fw-bold"
                },
                {
                    key: "unitCost",
                    label: "Unit Cost",
                    sortable: false,
                    thStyle: { width: "120px" },
                    tdClass: "text-center",
                    thClass: "text-center",
                    formatter: (value) => {
                        return value ? this.formatCurrency(value) : '';
                    }
                },
                {
                    key: "totalValue",
                    label: "Total Value",
                    sortable: false,
                    thStyle: { width: "140px" },
                    thClass: "text-center",
                    tdClass: "text-center fw-bold"
                },
                {
                    key: "actions",
                    label: "Actions",
                    sortable: false,
                    thStyle: { width: "100px" },
                    thClass: "text-center",
                    tdClass: "text-center"
                }
            ]
        };
    },
    computed: {
        ledgerData() {
            let runningBalance = 0;
            const sortedTransactions = [...this.transactions].sort((a, b) => 
                new Date(a.dateCreated) - new Date(b.dateCreated)
            );
            
            const processedTransactions = [];
            const parentTransactions = [];
            const childTransactions = [];
            
            // Separate parent and child transactions
            sortedTransactions.forEach(transaction => {
                if (!transaction.stockItemTransactionParentId || transaction.stockItemTransactionParentId === 0) {
                    parentTransactions.push(transaction);
                } else {
                    childTransactions.push(transaction);
                }
            });
            
            // Process each parent transaction and its children
            parentTransactions.forEach(parentTransaction => {
                const debit = parseFloat(parentTransaction.debit) || 0;
                const credit = parseFloat(parentTransaction.credit) || 0;
                runningBalance += debit - credit;
                
                const unitCost = parseFloat(parentTransaction.unitCost) || 0;
                const totalValue = runningBalance * unitCost;
                
                // Add parent transaction
                processedTransactions.push({
                    ...parentTransaction,
                    date: parentTransaction.dateCreated,
                    balance: this.formatNumber(runningBalance),
                    totalValue: this.formatCurrency(totalValue),
                    isParent: true
                });
                
                // Find and add child transactions for this parent
                const relatedChildren = childTransactions.filter(child => 
                    child.stockItemTransactionParentId === parentTransaction.id
                );
                
                // Sort children by date
                relatedChildren.sort((a, b) => new Date(a.dateCreated) - new Date(b.dateCreated));
                
                relatedChildren.forEach(childTransaction => {
                    const childDebit = parseFloat(childTransaction.debit) || 0;
                    const childCredit = parseFloat(childTransaction.credit) || 0;
                    runningBalance += childDebit - childCredit;
                    
                    const childUnitCost = parseFloat(childTransaction.unitCost) || 0;
                    const childTotalValue = runningBalance * childUnitCost;
                    
                    processedTransactions.push({
                        ...childTransaction,
                        date: childTransaction.dateCreated,
                        balance: this.formatNumber(runningBalance),
                        totalValue: this.formatCurrency(childTotalValue),
                        isChild: true,
                        parentId: parentTransaction.id
                    });
                });
            });
            
            // Add any orphaned child transactions (children without parents)
            const orphanedChildren = childTransactions.filter(child => 
                !parentTransactions.find(parent => parent.id === child.stockItemTransactionParentId)
            );
            
            orphanedChildren.forEach(orphanedChild => {
                const debit = parseFloat(orphanedChild.debit) || 0;
                const credit = parseFloat(orphanedChild.credit) || 0;
                runningBalance += debit - credit;
                
                const unitCost = parseFloat(orphanedChild.unitCost) || 0;
                const totalValue = runningBalance * unitCost;
                
                processedTransactions.push({
                    ...orphanedChild,
                    date: orphanedChild.dateCreated,
                    balance: this.formatNumber(runningBalance),
                    totalValue: this.formatCurrency(totalValue),
                    isOrphan: true
                });
            });
            
            return processedTransactions;
        },
        filteredData() {
            if (!this.filter.trim()) {
                return this.ledgerData;
            }
            
            return this.ledgerData.filter(item => {
                const searchTerm = this.filter.toLowerCase();
                return (
                    (item.referenceNumber && item.referenceNumber.toLowerCase().includes(searchTerm)) ||
                    (item.description && item.description.toLowerCase().includes(searchTerm))
                );
            });
        },
        totalRows() {
            return this.filteredData.length;
        },
        currentBalance() {
            if (this.ledgerData.length === 0) return 0;
            const lastTransaction = this.ledgerData[this.ledgerData.length - 1];
            return parseFloat(lastTransaction.balance.replace(/,/g, '')) || 0;
        },
        totalDebit() {
            return this.transactions.reduce((sum, t) => sum + (parseFloat(t.debit) || 0), 0);
        },
        totalCredit() {
            return this.transactions.reduce((sum, t) => sum + (parseFloat(t.credit) || 0), 0);
        }
    },
    methods: {
        formatNumber(value) {
            if (!value && value !== 0) return '';
            return parseFloat(value).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        },
        formatCurrency(value) {
            if (!value && value !== 0) return '';
            return 'Php ' + parseFloat(value).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        },
        printLedger() {
            this.$emit('print-ledger', this.stock);
        },
        addDebit() {
            this.$emit('add-debit', this.stock);
        },
        addCredit(transaction) {
            this.$emit('add-credit', { transaction, stock: this.stock });
        },
        editTransaction(transaction) {
            this.$emit('edit-transaction', { transaction, stock: this.stock });
        },
        deleteTransaction(transaction) {
            this.$emit('delete-transaction', {  transaction, stock: this.stock } );
        },
        generateRIS(transaction) {
            this.$emit('print-ris', transaction);
        },
        printStockCard() {
            this.$emit('print-stock-card', this.stock);
        },
        formatDateTime(dateTime) {
            if (!dateTime) return '';
            if(dateTime.includes('0001-01-01')) return '';
            return this.formatDateWithTime(new Date(dateTime));
        },
        downloadTemplate(type) {
            this.$emit('download-template', type);
        },
        uploadTransaction(type, parent) {
            if(type == 'debit'){
                this.$emit('upload-transaction', {
                    type,
                    stock: this.stock,
                    parent: null
                });
            }else{
                this.$emit('upload-transaction', {
                    type,
                    stock: this.stock,
                    parent: parent
                });
            }
        }
    }
};
</script>

<template>
    <div class="stock-ledger">
        <!-- Header -->
        <div class="card shadow-none">
            <div class="card-header">
                <div class="row align-items-center">
                    <div class="col">
                        <h5 class="card-title mb-0">
                            Stock Ledger - {{ stock.name }}
                        </h5>
                        <small class="text-muted">
                            Stock Number: {{ stock.stockNumber }} | 
                            Category: {{ stock.itemCategory }} | 
                            Unit: {{ stock.unit }}
                        </small>
                    </div>
                    <div class="col-auto">
                        <!-- <button 
                            type="button" 
                            class="btn btn-outline-success btn-sm me-1"
                            @click="exportToExcel"
                        >
                            <i class="bx bx-download me-1"></i>
                            Export
                        </button> -->
                        <button 
                            type="button" 
                            class="btn btn-outline-primary btn-sm"
                            @click="printStockCard"
                        >
                            <i class="bx bx-printer me-1"></i>
                            Print Stock Card
                        </button>
                    </div>
                </div>
            </div>
            
            <div class="card-body">
                <!-- Summary Cards -->
                <div class="row mb-2">
                    <div class="col-md-3">
                        <div class="card border-success shadow-none border">
                            <div class="card-body p-3">
                                <div class="d-flex justify-content-between">
                                    <div>
                                        <h6 class="text-success mb-1">Total Debit</h6>
                                        <h4 class="mb-0">{{ formatNumber(totalDebit) }}</h4>
                                    </div>
                                    <div class="avatar-sm">
                                        <span class="avatar-title rounded-circle bg-success">
                                            <i class="bx bx-plus-circle font-size-18"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="col-md-3">
                        <div class="card border-danger shadow-none border">
                            <div class="card-body p-3">
                                <div class="d-flex justify-content-between">
                                    <div>
                                        <h6 class="text-danger mb-1">Total Credit</h6>
                                        <h4 class="mb-0">{{ formatNumber(totalCredit) }}</h4>
                                    </div>
                                    <div class="avatar-sm">
                                        <span class="avatar-title rounded-circle bg-danger">
                                            <i class="bx bx-minus-circle font-size-18"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="col-md-3">
                        <div class="card border-info shadow-none border">
                            <div class="card-body p-3">
                                <div class="d-flex justify-content-between">
                                    <div>
                                        <h6 class="text-info mb-1">Current Balance</h6>
                                        <h4 class="mb-0">{{ formatNumber(currentBalance) }}</h4>
                                    </div>
                                    <div class="avatar-sm">
                                        <span class="avatar-title rounded-circle bg-info">
                                            <i class="bx bx-package font-size-18"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="col-md-3">
                        <div class="card border-warning shadow-none border">
                            <div class="card-body p-3">
                                <div class="d-flex justify-content-between">
                                    <div>
                                        <h6 class="text-warning mb-1">Transactions</h6>
                                        <h4 class="mb-0">{{ transactions.length }}</h4>
                                    </div>
                                    <div class="avatar-sm">
                                        <span class="avatar-title rounded-circle bg-warning">
                                            <i class="bx bx-list-ul font-size-18"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Filter and Controls -->
                <div class="row mb-3">
                    <div class="col-md-6">
                        <div class="d-flex gap-2">
                            <b-form-input
                                v-model="filter"
                                type="text"
                                placeholder="Search transactions..."
                                style="width: 300px;"
                            ></b-form-input>
                            <b-form-select
                                class="form-select"
                                v-model="perPage"
                                :options="pageOptions"
                                style="width: 80px;"
                            ></b-form-select>
                        </div>
                    </div>
                    <div class="col-md-6 text-right">
                        <b-dropdown
                            id="dropdown-dropleft"
                            variant="link"
                            toggle-class="text-decoration-none px-0"
                            no-caret
                        >
                            <template #button-content>
                                <button class="btn btn-info me-1"><i class="bx bx-download"></i> Download Template</button>
                            </template>
                            <b-dropdown-item
                                @click="downloadTemplate('debit')"
                            >
                                Debit Transaction
                            </b-dropdown-item>
                            <b-dropdown-item
                                @click="downloadTemplate('credit')"
                            >
                                Credit Transaction
                            </b-dropdown-item>
                        </b-dropdown>
                        <button class="btn btn-success me-1" @click="uploadTransaction('debit')"><i class="bx bx-upload"></i> Upload Debit Transaction</button>
                        <button class="btn btn-primary" @click="addDebit"><i class="bx bx-plus"></i> Add Debit</button>
                    </div>
                </div>
                <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                    <p class="mt-2 mb-0">Loading transactions...</p>
                </div>

                <div v-else>
                    <b-table
                        :items="filteredData"
                        :fields="fields"
                        :current-page="currentPage"
                        :per-page="perPage"
                        :busy="loading"
                        responsive
                        show-empty
                        empty-text="No transactions found"
                        class="ledger-table"
                    >
                         <template #cell(date)="data">
                            <div class="position-relative">
                                <RawData :data="data.item" v-if="$store.state.decrypt.isAdmin()" />
                                {{ formatDateTime(data.item.dateTime) }}   
                            </div>
                         </template>
                         <template #cell(referenceNumber)="data">
                            <div>
                                <span>{{data.item.debit > 0 ? 'P.O No: ' : 'RIS No: ' }}</span>{{data.value}}
                                <small class="d-block" v-if="data.item.debit > 0 && (!data.item.stockItemTransactionParentId || data.item.stockItemTransactionParentId == 0)">
                                    <a href="javascript:void(0)" :id="`credit-btn-${data.item.id}`"  class="hover-underline"> [Add Credit] </a>
                                    <b-popover
                                        :target="`credit-btn-${data.item.id}`"
                                        triggers="click blur"
                                        placement="right bottom"
                                        variant="secondary"
                                        custom-class="p-0"
                                    >
                                        <b-list-group>
                                            <b-list-group-item
                                                button
                                                action
                                                class="d-flex align-items-center text-success"
                                                @click="addCredit(data.item)"
                                            >
                                                <i class="bx bx-plus font-size-18 me-1"></i>
                                                Manual Add
                                            </b-list-group-item>
                                            <b-list-group-item
                                                button
                                                action
                                                class="d-flex align-items-center text-info"
                                                @click="uploadTransaction('credit', data.item)"
                                                ><i class="bx bx-upload font-size-18 me-1"></i
                                                >Upload Excel</b-list-group-item
                                            >
                                        </b-list-group>
                                    </b-popover>
                                </small>
                            </div>
                        </template>


                        <template #cell(debit)="data">
                            <span v-if="data.value" class="text-green fw-bold">
                                {{ data.value }}
                            </span>
                        </template>

                        <template #cell(credit)="data">
                            <span v-if="data.value" class="text-orange fw-bold">
                                {{ data.value }}
                            </span>
                        </template>

                        <template #cell(balance)="data">
                            <span class="text-info fw-bold">
                                {{ data.value }}
                            </span>
                        </template>

                        <template #cell(totalValue)="data">
                            <span class="text-primary fw-bold">
                                {{ data.value }}
                            </span>
                        </template>

                        <template #cell(actions)="row">
                            <div class="d-flex justify-content-center gap-1">
                                <button
                                    type="button"
                                    class="btn btn-link p-0 action-btn edit-btn"
                                    @click="editTransaction(row.item)"
                                    title="Edit Transaction"
                                >
                                    <i class="bx bx-edit font-size-18"></i>
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-link p-0 action-btn delete-btn"
                                    @click="deleteTransaction(row.item)"
                                    title="Delete Transaction"
                                >
                                    <i class="bx bx-trash font-size-18 text-danger"></i>
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-link p-0 action-btn delete-btn"
                                    @click="generateRIS(row.item)"
                                    title="Generate RIS"
                                >
                                    <i class="bx bx-printer font-size-18 text-purple"></i>
                                </button>
                            </div>
                        </template>
                    </b-table>

                    <!-- Pagination -->
                    <div class="row" v-if="totalRows > perPage">
                        <div class="col">
                            <div class="dataTables_paginate paging_simple_numbers float-end">
                                <ul class="pagination pagination-rounded mb-0">
                                    <b-pagination
                                        v-model="currentPage"
                                        :total-rows="totalRows"
                                        :per-page="perPage"
                                        aria-controls="ledger-table"
                                    ></b-pagination>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>